import styled, { keyframes } from 'styled-components';
import cn from 'classnames';
import { useEffect } from 'react';
import styles from './Toast.module.scss';
import { Button } from '../../Button/Button';
import { Icon, IconProps } from '../../Icon/Icon';

export interface ToastProps {
  id?: string;
  title?: string;
  content: string;
  icon?: IconProps['type'];
  alt?: string;
  type?: 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary';
  time?: number;
  onClose?: () => void;
}

const timing = keyframes`
    0% {width: 100%;}
    100% {width: 0;}
  `;

const goUp = keyframes`
  0% {
    top: 25rem;
  }
  100% {
    top: 0;
  }
`;

const goRight = keyframes`
  0% {
    left: 0;
  }
  100% {
    left: 35rem;
  }
`;

const Timing = styled.div<{ $timing: number }>`
  animation: ${(props) => props.$timing}s ${timing} linear;
`;

const ToastStyled = styled.li<{ $entryTime?: number; $delayOutro?: number }>`
  animation: ${(props) => props.$entryTime}s ${goUp} linear,
    0.5s ${goRight} linear ${(props) => props.$delayOutro}s;
`;

export const Toast = ({
  title = undefined,
  content,
  icon = undefined,
  alt,
  onClose = () => {},
  time = 5,
  type = 'primary',
  id = '0',
}: ToastProps) => {
  useEffect(() => {
    const timeout = setTimeout(() => {
      onClose?.();
    }, time * 1000 + 500);

    return () => clearTimeout(timeout);
  }, []);

  const selectIcon = (): IconProps['type'] | undefined => {
    switch (type) {
      case 'success':
        return 'checked-thin' as IconProps['type'];
      case 'warning':
      case 'error':
        return 'warning' as IconProps['type'];
      case 'info':
        return 'info' as IconProps['type'];
      default:
        return undefined;
    }
  };

  const defaultIcon = icon || selectIcon();

  return (
    <ToastStyled
      $entryTime={0.3}
      $delayOutro={time}
      className={cn(styles.root, styles[type])}
    >
      <div className={styles.header}>
        <p className={styles.title}>{title}</p>
        <Button
          className={styles['button-close']}
          iconPlace="only-icon"
          icon="cancel"
          onClick={onClose}
        />
      </div>
      <div className={styles.content}>
        {defaultIcon && (
          <Icon classname={styles.icon} alt={alt} type={defaultIcon} />
        )}
        <p>{content}</p>
      </div>
      <Timing $timing={time} className={styles.timing} />
    </ToastStyled>
  );
};
